// [src/layouts/MainLayout.jsx]

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import BottomNavBar from 'components/layout/BottomNavBar/BottomNavBar';
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import PullToRefresh from 'react-simple-pull-to-refresh';
import useModal from 'hooks/useModal';
import { API_BASE_URL } from 'config/apiConfig';
import { getTokenStatus } from 'utils/tokenManager';

import AlarmModal from "components/ui/Modals/AlarmModal/AlarmModal";

import ArrowLeft from "assets/images/icon/arrow-left-icon.svg";
import alarmIcon from 'assets/images/icon/alarm-icon.svg';
import gearIcon from 'assets/images/icon/gear-icon.svg';

import 'styles/Font.css';

const Spinner = () => (
    <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        height: '100%', paddingTop: '110px',
    }}>
        <div className="loading-spinner" style={{
            width: '24px', height: '24px',
            border: '3px solid #f3f3f3', borderTop: '3px solid #8052FF',
            borderRadius: '50%', animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
);

// HOME 전용 그라데이션 & 기본 배경색
const HOME_GRADIENT = 'linear-gradient(180deg, rgba(2, 49, 251, 0.6) 0%, rgba(2, 49, 251, 0.888488) 7.43%, #091371 20.88%, #0D042D 32.47%)';
const DEFAULT_BACKGROUND = '#0D042D';

function MainLayout() {
    usePreventHorizontalScroll();
    const location = useLocation();
    const navigate = useNavigate();

    const [refreshKey, setRefreshKey] = useState(0);
    const [hasUnread, setHasUnread] = useState(false);
    const alarmModal = useModal();

    const checkUnreadStatus = useCallback(async () => {
        const { token } = getTokenStatus();
        if (!token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/pushList`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                const list = data.list || [];
                const hasNew = list.some(item => item.read === 'N');
                setHasUnread(hasNew);
            }
        } catch (error) {
            console.error("Alarm Check Error:", error);
        }
    }, []);

    useEffect(() => {
        checkUnreadStatus();
    }, [checkUnreadStatus]);

    const handleModalClose = () => {
        alarmModal.close();
        checkUnreadStatus();
    };

    /* 헤더 설정 */
    const headerConfig = useMemo(() => {
        const path = location.pathname;

        // HOME: 헤더 미표시 (여백 0)
        if (path === '/home' || path === '/') {
            return { type: 'HOME', showHeader: false, activeView: 'HOME' };
        }

        // 나머지: 헤더 표시
        if (path === '/sb') {
            return { type: 'MAIN', showHeader: true, title: 'BOOST', isTransparent: false, activeView: 'BOOST' };
        }

        if (path === '/pt') {
            return { type: 'MAIN', showHeader: true, title: 'PT', isTransparent: false, activeView: 'PT' };
        }

        if (path === '/ipt') {
            return { type: 'MAIN', showHeader: true, title: 'IPT', isTransparent: false, activeView: 'IPT' };
        }

        if (path === '/lylo') {
            return { type: 'MAIN', showHeader: true, title: 'LYLO', isTransparent: false, activeView: 'LYLO' };
        }

        if (path === '/my') {
            return {
                type: 'MAIN',
                showHeader: true,
                title: 'MY',
                isTransparent: false,
                hasSetting: true,
                activeView: 'MY'
            };
        }

        // 서브 페이지
        return { type: 'SUB', showHeader: true, title: '', isTransparent: false, activeView: '' };
    }, [location.pathname]);

    const handleNotYetClick = (label) => {
        alert(`${label || "선택하신"} 서비스는 준비 중입니다.`);
    };

    const handleRefresh = async () => {
        setRefreshKey(prevKey => prevKey + 1);
        checkUnreadStatus();
        return new Promise(resolve => setTimeout(resolve, 500));
    };

    const renderHeader = () => {
        const { type, title, isTransparent, hasSetting, showHeader } = headerConfig;

        // HOME이면 헤더 렌더링 안 함
        if (!showHeader) return null;

        const wrapperStyle = {
            ...styles.headerArea,
            backgroundColor: isTransparent ? 'transparent' : '#0D042D',
        };

        if (type === 'SUB') {
            return (
                <header style={wrapperStyle}>
                    <div style={styles.headerContainer}>
                        <div onClick={() => navigate(-1)} style={styles.backButton}>
                            <img src={ArrowLeft} alt="뒤로가기" style={styles.backArrow} />
                        </div>
                        {title && <span style={styles.pageTitle}>{title}</span>}
                    </div>
                </header>
            );
        }

        return (
            <header style={wrapperStyle}>
                <div style={styles.mainHeaderRow}>
                    <div style={styles.leftElement}>
                        <span style={styles.titleHeaderText}>{title}</span>
                    </div>
                    <div style={styles.rightIconContainer}>
                        <div style={styles.iconWrapper} onClick={alarmModal.open}>
                            <img src={alarmIcon} alt="Alarm" style={styles.headerIcon} />
                            {hasUnread && <div style={styles.redDot} />}
                        </div>
                        {hasSetting && (
                            <img src={gearIcon} alt="Setting" style={styles.headerIcon}
                                onClick={() => navigate('/setting')} />
                        )}
                    </div>
                </div>
            </header>
        );
    };

    // [중요] 배경색 결정 로직
    const isHome = location.pathname === '/home' || location.pathname === '/';
    const currentBackground = isHome ? HOME_GRADIENT : DEFAULT_BACKGROUND;

    return (
        // 여기서 배경을 동적으로 적용
        <div style={{ ...styles.layoutWrapper, background: currentBackground }}>

            {renderHeader()}

            <div style={styles.contentArea}>
                <PullToRefresh
                    onRefresh={handleRefresh}
                    pullingContent={''}
                    refreshingContent={<Spinner />}
                    maxPullDownDistance={100}
                    backgroundColor={'transparent'}
                >
                    {/* HOME은 padding 0, 나머지는 헤더 높이만큼 띄움 */}
                    <div style={{
                        ...styles.scrollContainer,
                        paddingTop: headerConfig.showHeader ? '105px' : '0px',
                        background: 'transparent'
                    }}>
                        <Outlet key={refreshKey} />
                    </div>
                </PullToRefresh>
            </div>

            <div style={styles.bottomNavWrapper}>
                <BottomNavBar
                    activeView={headerConfig.activeView}
                    onNotYetClick={handleNotYetClick}
                />
            </div>

            <AlarmModal isOpen={alarmModal.isOpen} onClose={handleModalClose} />
        </div>
    );
}

const styles = {
    layoutWrapper: {
        width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        position: 'relative', overflow: 'hidden',
        // background는 인라인 스타일로 동적 적용됨
    },
    headerArea: {
        width: '100%', boxSizing: 'border-box', position: 'fixed', top: 0, left: 0, zIndex: 1000,
        paddingTop: 'max(env(safe-area-inset-top), 60px)',
        paddingBottom: '12px',
        paddingLeft: '24px', paddingRight: '24px',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        width: '100%',
        height: '31px',
    },
    mainHeaderRow: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', height: '31px',
    },
    leftElement: { display: 'flex', alignItems: 'center', height: '100%' },
    titleHeaderText: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '26px',
        color: '#FFFFFF',
        lineHeight: '31px'
    },
    rightIconContainer: { display: 'flex', alignItems: 'center', gap: '16px', height: '100%' },
    iconWrapper: {
        position: 'relative',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerIcon: { width: '24px', height: '24px', cursor: 'pointer', color: '#FCFCFA' },
    redDot: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '6px',
        height: '6px',
        backgroundColor: '#FF5252',
        borderRadius: '50%',
        zIndex: 10
    },
    backButton: {
        width: '24px',
        height: '24px',
        cursor: 'pointer',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    backArrow: { width: '7px', height: '14px' },
    pageTitle: { fontFamily: '"Pretendard", sans-serif', fontWeight: '700', fontSize: '18px', color: '#FCFCFA' },
    contentArea: { flex: 1, width: '100%', position: 'relative', overflow: 'hidden' },
    scrollContainer: {
        height: '100%', overflowY: 'auto', overflowX: 'hidden',
        paddingBottom: '80px',
        minHeight: '100%',
    },
    bottomNavWrapper: { position: 'fixed', bottom: 0, width: '100%', zIndex: 999, overscrollBehavior: 'none', }
};

export default MainLayout;